<template>
    <div class="competition-new" :class="{ 'content-brief-competition': isContentBrief }">
      <div class="container" v-if="snapshotsMissing">
        <div class="rank-zone__no-data">Looks like you don’t have any ranking data to report.</div>
      </div>
      <div class="container" v-else-if="!isDataFetched">
        <div class="rank-zone__loader">
          <base-loader/>
        </div>
      </div>
      <div class="container" v-else>
        <div class="competition-new__row">
          <base-content-block class="competition-new__row-item"
                              title="Comparison">
            <div v-if="!isPieChartData"
                 class="no-data-block">
              Not enough data for drawing a chart
            </div>
            <div class="competition-new__chart-wrapper" v-else-if="pieChartData">
              <PieChart isComparison :data="pieChartData.items" hasHeader class="charts__pie"/>
            </div>
          </base-content-block>
          <base-content-block class="competition-new__row-item"
                              title="First Page Impressions">
            <div v-if="!isBarChartData || !barChartData.list1.length"
                 class="no-data-block">
              Not enough data for drawing a chart
            </div>
            <div v-else
                 class="competition-new__chart-wrapper">
              <BarChart :list1="barChartData.list1"
                        :list2="barChartData.list2"
                        :date1="barChartData.date1"
                        :date2="barChartData.date2"
                        highlightFirst
                        class="charts__pie"/>
            </div>
          </base-content-block>
        </div>

        <div class="table-wrapper">
          <div class="table-wrapper__header competition-new__table-controls">
            <div class="competition-new__select-wrap">
              <div class="competition-new__select-holder competition-new__select-holder_main">
                <base-select class="competition-new__select"
                             v-model="filters.snapshot_date_1"
                             :showTopBorder="true"
                             :options="formattedSnapshots"
                             @change="onMainSnapshotChanged()"/>
              </div>
              <div class="competition-new__select-holder"
                   :class="{ 'competition-new__select-holder_comparing': filters.isComparing }">
                <base-select class="competition-new__select"
                             :style="{ 'width': filters.isComparing ? '180px' : '190px' }"
                             v-model="filters.snapshot_date_2"
                             :showTopBorder="true"
                             :options="formattedSnapshots"
                             :placeholderStyle="{
                               color: '#011627',
                               'margin-left': filters.isComparing ? 'auto' : '-19px'
                             }"
                             placeholder="Set Comparison Date"
                             @change="onComparingSnapshotChanged"/>
              </div>

            </div>
            <div class="competition-new__table-type-selection">
              <base-radio
                v-model="tableType"
                style="margin-right: 15px;"
                value="msv"
                label="MSV"
                class="competition-new__table-type-selection__radio-button"
                crossColor="#8F90A6"/>
              <base-radio
                v-model="tableType"
                value="keyword"
                label="# KW"
                class="competition-new__table-type-selection__radio-button"
                crossColor="#8F90A6"/>
            </div>
            <div class="competition-new__radio-wrap">
              <base-radio class="competition-new__radio"
                          label="Siblings"
                          v-model="filters.type"
                          value="1"
                          @change="onCompetitorTypeChanged()"/>
              <base-radio class="competition-new__radio"
                          label="Competitors"
                          v-model="filters.type"
                          value="2"
                          @change="onCompetitorTypeChanged()"/>
              <base-radio class="competition-new__radio"
                          label="Both"
                          v-model="filters.type"
                          value="3"
                          @change="onCompetitorTypeChanged()"/>
            </div>
          </div>
          <div class="table-wrapper__body">
            <base-table-collapsible class="competition-new__table"
                                    :config="config"
                                    :data="tableData"
                                    :isShowLoader="comparisonDataUpdating"
                                    :sorted="false"
                                    :showSummary="false"
                                    :headerStyle="{ 'background-color': 'transparent' }"
                                    :count="tableItemCount"
                                    @cellClick="handleCellClick"
            />
          </div>
        </div>

      </div>
      <div v-if="isDataUpdating" class="page-cover-loader">
        <base-loader/>
      </div>
    <modal name="movement"
           v-bind="modalProps"
    >
      <CompareRankZoneModal :clientWebPropertyName="clientWebPropertyName"
                            :clientWebPropertyId="clientWebPropertyId"
                            :queryData="clickedItem"
                            @close="closeModal"/>
    </modal>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import PieChart from '@/features/reports/common/PieChart'
import BarChart from '@/features/reports/common/BarChart'
import CompareRankZoneModal from './CompareRankZoneModal'
import filters from '@/common/Filters'
import { convertAdvancedOptionsToFilters } from '@/features/reports/common/advanced-options/config'
import { baseComponent, topLevelTableConfig } from '@/features/reports/common/Mixins'
import { moveClientToStart, moveOtherToEnd } from '@/features/reports/common/Helper'
import baseModalProps from '@/common/BaseModalProps'
import moment from 'moment'
import { cancelTokens } from '@/services/httpService'

/**
 * Component for comparison data. Used in RankZone
 */
export default {
  name: 'Competition',
  mixins: [baseComponent, topLevelTableConfig],
  components: {
    PieChart,
    BarChart,
    CompareRankZoneModal
  },
  created () {
    this.prepareDataProperties()
  },
  beforeDestroy () {
    this.resetPageData()
  },
  props: {
    /**
     * Advanced options from parent element
     */
    selectedAdvancedOptions: {
      type: Array,
      default: () => []
    },
    urlId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isDataExists: true,
      comparisonDataUpdating: false,
      // table filters
      filters: {
        snapshot_date_1: null,
        snapshot_date_2: -1,
        // set default company type - Both
        type: '3',
        isComparing: false
      },
      // page specific table config
      topLevelConfig: {
        highlightProp: 'type',
        highlightValue: 'Siblings',
        colorMap: [],
        colorProperty: 'date',
        children: null
      },
      // snapshot list for the snapshot dropdowns
      tableType: 'msv',
      clickedItem: {}
    }
  },
  computed: {
    ...mapState('rankZoneCompetition', {
      /**
       * fetch table data from store depending on comparing mode
       * */
      tableData: function (state) {
        let tableData = []
        try {
          if (this.filters.isComparing) {
            tableData = [...state.competition].map(item => {
              const obj = {}
              Object.assign(obj, item, item[this.tableType])
              obj.tooltipType = this.tableType === 'msv' ? 'keyword' : 'msv'
              return obj
            })
          } else {
            tableData = [...state.mainData.competition].map(item => {
              const obj = {}
              Object.assign(obj, item, item[this.tableType])
              obj.tooltipType = this.tableType === 'msv' ? 'keyword' : 'msv'
              return obj
            })
          }
          return tableData.sort((a, b) => {
            if ((a.type === 'Clients' && b.type === 'Siblings') || (a.type === 'Siblings' && b.type === 'Competitors')) {
              return -1
            } else if ((b.type === 'Clients' && a.type === 'Siblings') || (b.type === 'Siblings' && a.type === 'Competitors')) {
              return 1
            } else {
              return 0
            }
          })
        } catch {
          return []
        }
      },
      /**
       * process store data for Pie chart
       * */
      pieChartData: function (state) {
        let total = 0
        let items = state.mainData.competition_pie_chart.map(item => {
          total += item.msv ? item.msv.total_msv : 0
          return {
            name: item.label,
            value: item.msv ? item.msv.total_msv : 0,
            intermediate: item.rank,
            type: item.type
          }
        })
        items = moveClientToStart(items)
        items = moveOtherToEnd(items)
        state.mainData.share_of_voice.forEach(el => {
          let idx = items.findIndex(item => item.name === el.name)
          if (idx > -1) {
            items[idx].share_of_voice = el.value
          }
        })
        return {
          items,
          footerItem: {
            name: 'Est. Monthly Traffic:',
            value: total
          }
        }
      },
      isPieChartData () {
        return this.pieChartData.items && this.pieChartData.items.length
      },
      /**
       * process store data for Bar chart
       * */
      barChartData: function (state) {
        const chartData = {
          list1: [],
          list2: [],
          date1: this.$options.filters.formattedDate(this.filters.snapshot_date_1),
          date2: this.$options.filters.formattedDate(this.filters.snapshot_date_2)
        }
        if (this.filters.isComparing) {
          chartData.list1 = this.tableData.filter((i, index) => index % 2 === 0).map(i => ({
            name: i.label,
            value: i['01 - 03'] + i['04 - 07'] + i['08 - 10']
          }))
          chartData.list2 = this.tableData.filter((i, index) => index % 2 !== 0).map(i => ({
            name: i.label,
            value: i['01 - 03'] + i['04 - 07'] + i['08 - 10']
          }))
        } else {
          chartData.list1 = moveClientToStart(state.mainData.share_of_voice, state.mainData.competition)
          chartData.list1 = moveOtherToEnd(chartData.list1)
        }

        return chartData
      },
      isBarChartData () {
        return (this.barChartData.list1 && this.barChartData.list1.length) || (this.barChartData.list2 && this.barChartData.list2.length)
      }
    }),
    ...mapState('rankZoneCommon', {
      clientWebProperty (state) {
        const webPropertyGroups = _.cloneDeep(state.webPropertyGroups)
        return (webPropertyGroups.find(i => i.type === 'Clients') || {})
      },
      clientWebPropertyName (state) {
        return this.clientWebProperty.label
      },
      clientWebPropertyId (state) {
        return this.clientWebProperty._id
      },
      webPropertyGroups: 'webPropertyGroups'
    }),
    /**
     * there is only one level table - without child
     * */
    config () {
      return [
        this.topLevelConfig
      ]
    },
    isContentBrief () {
      return !!this.urlId.length
    },
    formattedSnapshots () {
      const formattedSnapshots = this.snapshots.map(snapshot => ({
        label: this.$options.filters.formattedDate(snapshot),
        value: snapshot
      }))
      this.filters.isComparing && formattedSnapshots.unshift({
        label: 'Remove Comparison Date',
        value: -1
      })
      return formattedSnapshots
    },
    advancedOptionsToFilters () {
      return convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
    },
    modalProps () {
      const shareModalProps = {
        width: '1200px'
      }
      return { ...baseModalProps, ...shareModalProps }
    },
    /**
     * number items for table subtitle
     * */
    tableItemCount () {
      const uniqItems = this.tableData.reduce((acc, item) => {
        if (!acc.includes(item._id)) {
          acc.push(item._id)
        }
        return acc
      }, [])
      return uniqItems.length
    }
  },
  watch: {
    selectedClient (client) {
      if (!client) {
        return
      }
      // reset all data after changing client
      this.isDataFetched = false
      this.filters.isComparing = false
      this.filters.snapshot_date_1 = null
      this.filters.snapshot_date_2 = -1
      this.topLevelConfig.colorMap = []
      // set default company type - Both
      this.filters.type = '3'
      this.resetPageData()
    },
    selectedAdvancedOptions: {
      async handler () {
        this.isDataFetched && await this.updateData()
      },
      deep: true
    },
    snapshots: {
      /**
       * receiving new snapshots from store is the trigger for load page data
       * */
      handler () {
        if (!this.selectedClient || !this.snapshotsPresent) {
          return
        }
        // check copied advanced filters and load data if it is allowed
        this.$nextTick(this.loadPageData)
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('rankZoneCompetition', [
      'getCompetitionData',
      'resetPageData',
      'resetComparisonData'
    ]),
    async loadPageData () {
      this.mapSnapshots()
      this.prepareTopLevelColorSettings()
      // set default company type - Both
      this.filters.type = '3'
      await this.updateData()
    },
    onMainSnapshotChanged () {
      if (this.filters.snapshot_date_1 === -1) {
        this.filters.snapshot_date_1 = this.filters.snapshot_date_2 !== -1 ? this.filters.snapshot_date_2 : this.snapshots[0]
        this.filters.snapshot_date_2 = -1
        this.onComparingSnapshotChanged(-1)
      }
      this.prepareTopLevelColorSettings()
      this.updateData()
    },
    /**
     * change client/sibling/competitor type radiobutton
     * */
    async onCompetitorTypeChanged () {
      await this.updateData()
    },
    onComparingSnapshotChanged (v) {
      this.filters.isComparing = v !== -1
      if (!this.filters.isComparing) {
        this.resetComparisonData(this.clientId)
        this.comparisonDataUpdating = false
        return
      }
      this.prepareTopLevelColorSettings()
      this.updateData()
    },
    /**
     * process received snapshots: set default snapshots and prepare snapshot list for dropdown
     * */
    mapSnapshots () {
      if (!this.filters.snapshot_date_1) {
        this.filters.snapshot_date_1 = this.snapshots[0]
      }
    },
    closeModal () {
      this.$modal.hide('movement')
    },
    async updateData () {
      if (this.isDataFetched && !this.filters.isComparing) {
        this.isDataUpdating = true
      } else if (this.filters.isComparing) {
        this.comparisonDataUpdating = true
      }
      let filters = _.cloneDeep(this.filters)
      if (this.selectedAdvancedOptions && this.selectedAdvancedOptions.length) {
        const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
        filters = Object.assign({}, filters, { advancedFilters })
      }
      const isSuccess = await this.getCompetitionData({ clientId: this.clientId, filters, urlId: this.urlId })
      this.isDataUpdating = false
      isSuccess && (this.comparisonDataUpdating = false)
      this.isDataFetched = true
    },
    /**
     * prepare color settings to highlight rows related to specific snapshot
     * */
    prepareTopLevelColorSettings () {
      if (!this.snapshotsMissing) {
        if (this.filters.isComparing) {
          this.topLevelConfig.colorMap = [this.filters.snapshot_date_1, this.filters.snapshot_date_2]
        } else {
          this.topLevelConfig.colorMap = []
        }
      }
    },
    handleCellClick (cellData) {
      if (cellData.propName === 'total_msv') {
        return
      }
      let advancedFilters = {}
      if (this.selectedAdvancedOptions && this.selectedAdvancedOptions.length) {
        advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      }
      const snapshotDateOne = moment(this.filters.snapshot_date_1).format('YYYY-MM-DD')
      const snapshotDateTwo = moment(this.filters.snapshot_date_2).format('YYYY-MM-DD')
      this.clickedItem = {
        'web_property': cellData._id || (this.webPropertyGroups.find(i => i.label === cellData.name) || {})._id,
        'date': cellData.date || snapshotDateOne,
        'rank_zone': cellData.propName,
        webPropertyName: cellData.name,
        urlId: this.urlId,
        advancedFilters
      }
      if (this.filters.snapshot_date_2 && this.filters.snapshot_date_2 !== -1) {
        this.clickedItem['second_date'] = this.clickedItem.date === snapshotDateOne ? snapshotDateTwo : snapshotDateOne
      }
      this.$modal.show('movement')
    },
    /**
     * modify default table config
     */
    prepareDataProperties () {
      this.prepareTopLevelColorSettings()
      const labels = ['Property Group', '01 - 03', '04 - 07', '08 - 10', '11 - 25', '26 - 50', '51 - 100', 'NR', 'ND', 'Total']
      // here remove keyword_total column
      this.topLevelConfig.cells = this.topLevelConfig.cells.reduce((result, item, index) => {
        item.clickable = true
        if (item.propName !== 'keyword_total') {
          if (index === 0) {
            result.push({
              ...item,
              label: labels[index],
              subtitleSingle: 'group found',
              subtitlePlural: 'groups found'
            })
          } else {
            result.push({
              ...item,
              label: labels[index]
            })
          }
        }
        return result
      }, [])
      this.topLevelConfig.cells[0].style['flex-basis'] = '15%'
      this.topLevelConfig.cells[9].clickable = false
    }
  },
  filters: {
    ...filters
  }
}
</script>

<style scoped lang="scss">
  .competition-new {

    &__select {
      width: 160px;
      color: $rfx-dark-march;
    }

    &__table-type-selection {
      display: flex;
      align-items: center;
      margin-left: auto;
      margin-right: 75px;

      &__radio-button {
        cursor: pointer;

        &::v-deep .radio__label {
          font-size: 18px;
          font-weight: 500;
        }
      }
    }

    &__advanced {
      margin-bottom: 14px;
    }

    &__row {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
      margin-bottom: 14px;

      &-item {
        flex: 1 1 100%;
        overflow: hidden;

        &:first-child {
          margin-right: 14px;
        }
      }
    }

    &__chart-wrapper {
      width: 100%;
      padding: 15px 0 30px 40px;
      box-sizing: border-box;
    }

    &__table {
      flex: 1 1;
    }

    &__table-controls {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
    }

    &__radio-wrap {
      display: flex;
      align-items: center;
    }

    &__radio {
      margin-right: 14px;
    }

    &__select-wrap {
      display: flex;
      align-items: center;
      padding-left: 10px;
    }

    &__select-holder {
      position: relative;

      &::after {
        content: '';
        display: block;
        position: absolute;
        width: 6px;
        height: 6px;
        left: 14px;
        top: 50%;
        margin-top: -3px;
        border-radius: 50%;
        pointer-events: none;
      }

      &_main {
        margin-right: 14px;

        &::after {
          background-color: $rfx-bright-blue;
        }
      }

      &_comparing {

        &::after {
          background-color: $rfx-light-green;
        }
      }
    }
  }

  .content-brief-competition {
    margin-top: 14px;

    .container {
      padding: 0;
    }

    .table-wrapper {
      background-color: #fff;
      position: relative;
      display: flex;
      flex-direction: column;
      margin-bottom: 30px;
      border-radius: 4px;
      border: 1px solid rgba(1,22,39,.1);

      &__header {
        min-height: 60px;
        box-sizing: border-box;
        padding: 0 20px;
      }

      &__body {
        display: flex;
      }
    }

    .page-cover-loader {
      display: flex;
      position: fixed;
      top: 16px;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, .5);
      align-items: center;
      justify-content: center;
      z-index: 99999;
    }
  }
</style>
